<template>
  <div
    class="press-detail-container"
    :class="{ 'mobile-or-tablet': isMobileOrTablet }"
  >
    <div class="press-detail">
      <!-- 标题 -->
      <h1 class="press-title">{{ getCurrentLangValue(article, "title") }}</h1>

      <!-- 信息栏 -->
      <div class="press-meta">
        <p v-if="article.date">时间：{{ article.date }}</p>
        <p v-if="getCurrentLangValue(article, 'source')">
          媒体：{{ getCurrentLangValue(article, "source") }}
        </p>
        <p v-if="getCurrentLangValue(article, 'author')">
          记者：{{ getCurrentLangValue(article, "author") }}
        </p>
        <p v-if="article.originalUrl">
          原文地址：
          <a
            :href="article.originalUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="underline-link"
          >
            {{ getCurrentLangValue(article, "title") }}
          </a>
        </p>
      </div>
      <div class="press-divider"></div>

      <!-- 封面图 -->
      <div class="press-image-wrapper">
        <img :src="article.coverImage" alt="封面图" class="press-image" />
      </div>

      <!-- 正文 -->
      <div
        class="press-content"
        ref="htmlShow"
        v-html="getCurrentLangValue(article, 'content')"
      ></div>

      <!-- 视频（如有） -->
      <div v-if="article.videoUrl" class="press-video">
        <video
          :src="article.videoUrl"
          controls
          autoplay
          muted
          preload="metadata"
          disablePictureInPicture
          controlsList="nodownload noremoteplayback noplaybackrate"
          class="video-frame"
        ></video>
      </div>

      <!-- 上一篇 / 下一篇 按钮 -->
      <div class="press-navigation">
        <div
          v-if="prevArticle && prevArticle.id"
          @click="goToPrevArticle"
          class="nav-btn prev-btn"
        >
          <span class="nav-text">← 上一篇</span>
        </div>
        <div
          v-if="nextArticle && nextArticle.id"
          @click="goToNextArticle"
          class="nav-btn next-btn"
        >
          <span class="nav-text">下一篇 →</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "PressReleasesDetail",
  data() {
    return {
      isMobileOrTablet: false, // 用于判断是否为平板或移动端
      // 示例文章数据（实际应从 API 或 props 获取）
      article: {
        title: {
          cn: "",
          en: "",
        },
        date: "",
        source: {
          cn: "",
          en: "",
        },
        author: {
          cn: "",
          en: "",
        },
        originalUrl: "",
        coverImage: "",
        content: {
          cn: "",
          en: "",
        },
        videoUrl: "",
        id: "",
      },
      // 所有文章列表（用于导航）
      articles: [],
    };
  },
  computed: {
    currentLanguage() {
      return this.$store.state.isEn; // 0 为中文，1 为英文
    },
    currentIdx() {
      return this.articles.findIndex(
        (article) => article.id === this.article.id
      );
    },
    prevArticle() {
      const idx = this.currentIdx;
      if (idx === -1) return null; // 如果找不到当前文章，则没有上一篇/下一篇
      if (idx === 0) return this.articles[this.articles.length - 1]; // 首条 -> 最后一条
      return this.articles[idx - 1];
    },
    nextArticle() {
      const idx = this.currentIdx;
      if (idx === -1) return null; // 如果找不到当前文章，则没有上一篇/下一篇
      if (idx === this.articles.length - 1) return this.articles[0]; // 最后一条 -> 首条
      return this.articles[idx + 1];
    },
  },
  created() {
    // 检查路由参数是否存在
    const id = this.$route.query.id;
    if (!id) {
      console.error("缺少必要的参数: id");
      this.setDefaultArticle();
      return;
    }

    // 从路由参数获取文章 ID 并加载对应文章
    // 根据URL https://www.chunyangtai.com/visitorGuide/pressReleasesDetail?columnId=65&id=1
    // ID 是通过查询参数传递的，所以需要使用 $route.query 而不是 $route.params
    this.loadArticle();
    this.checkScreenSize();
    window.addEventListener("resize", this.checkScreenSize);
  },
  mounted() {
    // 初始化富文本内容字体大小
    this.updateFontSizeClass();
    // 监听窗口大小变化
    window.addEventListener("resize", this.updateFontSizeClass);
  },
  destroyed() {
    window.removeEventListener("resize", this.checkScreenSize);
    // 移除resize监听器
    window.removeEventListener("resize", this.updateFontSizeClass);
  },
  watch: {
    $route(to, from) {
      if (to.query.id !== from.query.id) {
        this.loadArticle();
        // 加载新文章后更新字体大小
        this.$nextTick(() => {
          this.updateFontSizeClass();
        });
      }
    },
    // 监听语言变化，重新加载数据
    "$store.state.isEn"() {
      this.loadArticle();
      // 加载新文章后更新字体大小
      this.$nextTick(() => {
        this.updateFontSizeClass();
      });
    },
  },
  methods: {
    // 获取当前语言环境的值
    getCurrentLangValue(item, fieldName) {
      if (!item[fieldName]) return "";

      const langIndex = this.currentLanguage; // 0 为中文，1 为英文
      const langKey = langIndex === 1 ? "en" : "cn";

      if (typeof item[fieldName] === "object" && item[fieldName] !== null) {
        return item[fieldName][langKey] || item[fieldName]["cn"] || "";
      } else {
        // 如果不是对象，直接返回原值
        return item[fieldName] || "";
      }
    },
    formatArticleData(articleData) {
      return {
        id: articleData.id,
        title: {
          cn: articleData.cnTitle,
          en: articleData.enTitle,
        },
        date: articleData.newsDate,
        source: {
          cn: articleData.mediaCn,
          en: articleData.mediaEn,
        },
        author: {
          cn: articleData.newsReporterCn,
          en: articleData.newsReporterEn,
        },
        originalUrl: articleData.newsOriginUrl,
        coverImage: articleData.imageCn,
        content: {
          cn: articleData.newsContentCn,
          en: articleData.newsContentEn,
        },
        videoUrl: articleData.videoCn,
      };
    },
    loadArticle() {
      const id = this.$route.query.id;
      const columnId = this.$route.query.columnId; // 获取columnId参数

      // 检查是否有有效的ID
      if (!id) {
        console.error("缺少文章ID");
        this.setDefaultArticle();
        return;
      }

      // 请求当前文章
      this.$api.get(
        "/news/list",
        {
          current: 1,
          pageSize: 1,
          id: id,
          columnId: columnId,
        },
        (articleRes) => {
          // 请求当前栏目下的所有文章用于导航定位
          this.$api.get(
            "/news/list",
            {
              current: 1,
              pageSize: 9999,
              columnId: columnId,
            },
            (listRes) => {
              // 检查响应是否存在且有效
              if (!articleRes || typeof articleRes.code === "undefined") {
                console.error("获取新闻详情失败，响应格式错误", articleRes);
                this.setDefaultArticle();
                return;
              }

              if (!listRes || typeof listRes.code === "undefined") {
                console.error("获取新闻列表失败，响应格式错误", listRes);
                this.setDefaultArticle();
                return;
              }

              // 处理当前文章数据
              if (
                articleRes.code === 0 &&
                articleRes.data &&
                articleRes.data.length > 0
              ) {
                const articleData = articleRes.data[0];
                this.article = this.formatArticleData(articleData);

                // 更新富文本内容的字体大小
                this.$nextTick(() => {
                  this.updateFontSizeClass();
                });
              } else {
                console.error(
                  "获取新闻详情失败",
                  articleRes && articleRes.msg ? articleRes.msg : "未知错误"
                );
                this.setDefaultArticle();
              }

              // 处理文章列表用于导航
              if (listRes.code === 0 && listRes.data) {
                this.articles = listRes.data
                  .map((item) => ({
                    id: item.id,
                    title: {
                      cn: item.cnTitle,
                      en: item.enTitle,
                    },
                  }))
                  .sort((a, b) => a.id - b.id); // 按ID排序
              }
            }
          );
        }
      );
    },
    setDefaultArticle() {
      // 设置一个默认的空文章
      this.article = {
        title: {
          cn: "未找到文章",
          en: "Article Not Found",
        },
        date: "",
        source: {
          cn: "",
          en: "",
        },
        author: {
          cn: "",
          en: "",
        },
        originalUrl: "",
        coverImage: "",
        content: {
          cn: "<p>抱歉，未找到指定的文章。</p>",
          en: "<p>Sorry, the specified article could not be found.</p>",
        },
        videoUrl: "",
        id: "",
      };
      // 更新富文本内容的字体大小
      this.$nextTick(() => {
        this.updateFontSizeClass();
      });
    },
    goToPrevArticle() {
      if (this.prevArticle && this.prevArticle.id) {
        const currentQuery = { ...this.$route.query }; // 复制当前查询参数
        this.$router.push({
          path: "/visitorGuide/pressReleasesDetail",
          query: {
            ...currentQuery, // 保留当前的所有查询参数
            id: this.prevArticle.id, // 只更新id参数
          },
        });
      }
    },
    goToNextArticle() {
      if (this.nextArticle && this.nextArticle.id) {
        const currentQuery = { ...this.$route.query }; // 复制当前查询参数
        this.$router.push({
          path: "/visitorGuide/pressReleasesDetail",
          query: {
            ...currentQuery, // 保留当前的所有查询参数
            id: this.nextArticle.id, // 只更新id参数
          },
        });
      }
    },
    // 检查屏幕尺寸
    checkScreenSize() {
      this.isMobileOrTablet = window.innerWidth <= 968;
    },
    // 更新富文本内容的字体大小
    updateFontSizeClass() {
      const screenWidth = window.innerWidth;
      const htmlShowElement = this.$refs.htmlShow;

      if (!htmlShowElement) return;

      // 清除现有样式
      htmlShowElement.style.fontSize = "";
      const elements = htmlShowElement.querySelectorAll("*");
      elements.forEach((el) => {
        el.style.fontSize = "";
        el.style.lineHeight = "";
      });

      // 根据屏幕宽度设置字体大小
      if (screenWidth <= 960) {
        // 小屏幕
        htmlShowElement.style.fontSize = "14px";
        const paragraphs = htmlShowElement.querySelectorAll("p");
        paragraphs.forEach((p) => {
          p.style.fontSize = "14px";
          p.style.lineHeight = "24px";
          p.style.marginBottom = "12px";
        });

        const headings = htmlShowElement.querySelectorAll(
          "h1, h2, h3, h4, h5, h6"
        );
        headings.forEach((h, index) => {
          switch (h.tagName) {
            case "H1":
              h.style.fontSize = "24px";
              break;
            case "H2":
              h.style.fontSize = "20px";
              break;
            case "H3":
              h.style.fontSize = "18px";
              break;
            case "H4":
              h.style.fontSize = "16px";
              break;
            case "H5":
              h.style.fontSize = "14px";
              break;
            case "H6":
              h.style.fontSize = "12px";
              break;
          }
          h.style.margin = "16px 0";
          h.style.fontWeight = "bold";
        });

        const images = htmlShowElement.querySelectorAll("img");
        images.forEach((img) => {
          img.style.maxWidth = "100%";
          img.style.height = "auto";
          img.style.marginBottom = "0px";
        });
      } else if (screenWidth <= 1440) {
        // 中等屏幕
        htmlShowElement.style.fontSize = "16px";
        const paragraphs = htmlShowElement.querySelectorAll("p");
        paragraphs.forEach((p) => {
          p.style.fontSize = "16px";
          p.style.lineHeight = "28px";
          p.style.marginBottom = "14px";
        });

        const headings = htmlShowElement.querySelectorAll(
          "h1, h2, h3, h4, h5, h6"
        );
        headings.forEach((h, index) => {
          switch (h.tagName) {
            case "H1":
              h.style.fontSize = "28px";
              break;
            case "H2":
              h.style.fontSize = "24px";
              break;
            case "H3":
              h.style.fontSize = "20px";
              break;
            case "H4":
              h.style.fontSize = "18px";
              break;
            case "H5":
              h.style.fontSize = "16px";
              break;
            case "H6":
              h.style.fontSize = "14px";
              break;
          }
          h.style.margin = "16px 0";
          h.style.fontWeight = "bold";
        });

        const images = htmlShowElement.querySelectorAll("img");
        images.forEach((img) => {
          img.style.maxWidth = "100%";
          img.style.height = "auto";
          img.style.marginBottom = "18px";
        });
      } else {
        // 大屏幕
        htmlShowElement.style.fontSize = "18px";
        const paragraphs = htmlShowElement.querySelectorAll("p");
        paragraphs.forEach((p) => {
          p.style.fontSize = "18px";
          p.style.lineHeight = "32px";
          p.style.marginBottom = "16px";
        });

        const headings = htmlShowElement.querySelectorAll(
          "h1, h2, h3, h4, h5, h6"
        );
        headings.forEach((h, index) => {
          switch (h.tagName) {
            case "H1":
              h.style.fontSize = "32px";
              break;
            case "H2":
              h.style.fontSize = "28px";
              break;
            case "H3":
              h.style.fontSize = "24px";
              break;
            case "H4":
              h.style.fontSize = "20px";
              break;
            case "H5":
              h.style.fontSize = "18px";
              break;
            case "H6":
              h.style.fontSize = "16px";
              break;
          }
          h.style.margin = "16px 0";
          h.style.fontWeight = "bold";
        });

        const images = htmlShowElement.querySelectorAll("img");
        images.forEach((img) => {
          img.style.maxWidth = "100%";
          img.style.height = "auto";
          img.style.marginBottom = "26px";
        });
      }
    },
  },
};
</script>

<style scoped>
.press-detail-container {
  max-width: 1400px;
  margin: 120px auto 20px;
  font-family: "Microsoft YaHei", sans-serif;
}
.press-detail {
  width: 75%;
}

.press-title {
  font-size: 2rem;
  color: #333;
  margin-bottom: 16px;
  text-align: left;
}

.press-meta {
  padding: 12px 0;
  border-radius: 8px;
  margin-bottom: 10px;
  line-height: 1.6;
}

.press-meta p {
  margin: 6px 0;
  font-family: SourceHanSansCN-Regular;
  font-size: 18px;
  color: #98989f;
  letter-spacing: 0;
  text-align: left;
  font-weight: 400;
}
.press-meta p a {
  margin: 6px 0;
  font-family: SourceHanSansCN-Regular;
  font-size: 18px;
  color: #98989f;
  letter-spacing: 0;
  text-align: left;
  font-weight: 400;
}
.press-meta p a:hover {
  color: #ca9d76;
}
.press-divider {
  width: 99%;
  height: 1px;
  background: #d8d8d8;
  margin-bottom: 30px;
}

.press-image-wrapper {
  margin: 20px 0;
}

.press-image {
  width: 100%;
  height: auto;
  max-height: 638px;
  max-width: 100%;
  border-radius: 2px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.press-content {
  line-height: 1.8;
  color: #333;
  margin-bottom: 20px;
}

.press-content img {
  max-width: 100%;
  height: auto;
  margin: 10px 0;
}

.press-video {
  background-color: #000;
  margin: 40px 0 80px;
}

.video-frame {
  width: 100%;
  height: auto;
  max-height: 540px;
  border: none;
  border-radius: 8px;
}

.press-navigation {
  display: flex;
  justify-content: space-between;
  margin-top: 30px;
}

.nav-btn {
  padding: 10px 20px;
  color: rgb(0, 0, 0);
  text-decoration: none;
  border-radius: 4px;
  font-size: 1rem;
  transition: background-color 0.3s;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.prev-btn {
  text-align: left;
}

.next-btn {
  text-align: right;
}
.nav-text {
  font-size: 18px;
}

@media (max-width: 1541px) {
  .press-detail-container {
    width: 91%;
    margin: 120px auto 30px;
  }
}
@media (max-width: 1441px) {
  .press-detail-container {
    width: 92%;
    margin: 120px auto 30px;
  }
}
/* 平板设备样式 */
@media (max-width: 968px) {
  .press-detail-container.mobile-or-tablet {
    margin-top: 80px;
    padding: 15px;
  }
  .press-detail {
    width: 92%;
  }

  .press-title {
    font-size: 1.6rem;
  }

  .press-meta {
    padding: 10px 0;
    margin-bottom: 15px;
  }

  .press-meta p {
    margin: 5px 0;
    font-size: 14px;
  }
  .press-meta p a {
    font-size: 14px;
  }
  .press-divider {
    margin-bottom: 10px;
  }
  .press-image-wrapper {
    margin: 20px 0;
  }

  .press-content {
    font-size: 15px;
    line-height: 1.7;
  }

  .press-navigation {
    gap: 10px;
    align-items: center;
  }

  .nav-btn {
    width: 100%;
    text-align: center;
  }

  .prev-btn {
    width: 100%;
    text-align: left;
  }
  .next-btn {
    width: 100%;
    text-align: right;
  }
  .nav-text {
    width: 100%;
  }
}

/* 移动设备样式 */
@media (max-width: 768px) {
  .press-detail-container.mobile-or-tablet {
    margin-top: 90px;
    padding: 10px 5px;
  }

  .press-title {
    font-size: 1.4rem;
  }

  .press-meta {
    padding: 8px 0;
    margin-bottom: 10px;
  }
  .press-divider {
    margin-bottom: 10px;
  }
  .press-meta p {
    margin: 4px 0;
    font-size: 13px;
  }
  .press-meta p a {
    font-size: 13px;
  }
  .press-content {
    font-size: 14px;
    line-height: 1.6;
  }

  .press-image {
    max-width: 100%;
  }

  .video-frame {
    height: 250px;
  }
}

/* 小型移动设备样式 */
@media (max-width: 420px) {
  .press-detail-container.mobile-or-tablet {
    margin-top: 70px;
    padding: 5px;
  }

  .press-title {
    font-size: 1.2rem;
  }

  .press-meta p {
    font-size: 12px;
  }
  .press-meta p a {
    font-size: 12px;
  }

  .press-content {
    font-size: 13px;
  }

  .press-navigation {
    margin-top: 20px;
  }

  .nav-btn {
    padding: 8px 15px;
    font-size: 0.9rem;
  }
}
.underline-link {
  text-decoration: underline;
}
</style>